"use client"

import { useState, useEffect, useCallback } from "react"
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts"
import DashboardLayout from "../../components/Layout/DashboardLayout"
import Breadcrumbs from "../../components/Layout/Breadcrumbs"
import MiniFooter from "../../components/Layout/miniFooter"
import api from "../../services/api"
import WhatsAppChat from "../../components/support/WhatsAppChat"

const BRAND = {
  primary: "#3B82F6",
  primarySoft: "rgba(59, 130, 246, 0.1)",
  success: "#22C55E",
  warning: "#F59E0B",
  danger: "#EF4444",
  muted: "#6B7280",
  text: "#1F2937",
}

export default function AnalyticsPage() {
  const [loading, setLoading] = useState(true)
  const [dateRange, setDateRange] = useState("30d")
  const [analytics, setAnalytics] = useState(null)

  const breadcrumbItems = [{ name: "Dashboard", path: "/user-dashboard" }, { name: "Analytics" }]

  const fetchAnalytics = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get(`/links/analytics/user-analytics?range=${dateRange}`)
      setAnalytics(res.data.data || {})
    } catch (err) {
      console.error("Failed to fetch analytics:", err)
    } finally {
      setLoading(false)
    }
  }, [dateRange])

  useEffect(() => {
    fetchAnalytics()
  }, [fetchAnalytics])

  const calculateTrend = (current, previous) => {
    if (!previous) return { value: 0, trend: "neutral" }
    const diff = current - previous
    const percent = previous ? Math.round((diff / previous) * 100) : 100
    return { value: Math.abs(percent), trend: diff > 0 ? "up" : diff < 0 ? "down" : "neutral" }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
          <div className="spinner-border" style={{ color: BRAND.primary, width: "3rem", height: "3rem" }} />
          <p className="mt-3" style={{ color: BRAND.muted }}>Loading analytics...</p>
        </div>
      </DashboardLayout>
    )
  }

  const {
    totalClicks = 0,
    totalLinks = 0,
    totalQrScans = 0,
    activeLinks = 0,
    dailyClicks = [],
    topLinks = [],
    previousPeriodClicks = 0,
  } = analytics || {}

  const clicksTrend = calculateTrend(totalClicks, previousPeriodClicks)
  const avgClicksPerLink = totalLinks ? Math.round(totalClicks / totalLinks) : 0

  return (
    <DashboardLayout>
      <style>{`
        .analytics-container { background: linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%); min-height: 100vh; position: relative; overflow: hidden; }
        .decorative-orb { position: absolute; border-radius: 50%; filter: blur(60px); pointer-events: none; }
        .page-header-analytics { background: ${BRAND.primary}; border-radius: 16px; padding: 2rem; margin-bottom: 2rem; color: white; box-shadow: 0 10px 30px ${BRAND.primarySoft}; }
        .stat-card { background: white; border: 1px solid #e5e7eb; border-radius: 16px; padding: 1.5rem; box-shadow: 0 4px 12px rgba(0,0,0,0.05); transition: all 0.3s ease; cursor: pointer; height: 100%; }
        .stat-card:hover { transform: translateY(-4px); box-shadow: 0 8px 24px rgba(0,0,0,0.1); }
        .stat-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; margin-bottom: 1rem; }
        .chart-card { background: white; border: 1px solid #e5e7eb; border-radius: 16px; padding: 1.5rem; box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
        .trend-badge { padding: 4px 12px; border-radius: 20px; font-size: 0.875rem; font-weight: 600; }
        .trend-up { background: rgba(34, 197, 94, 0.1); color: ${BRAND.success}; }
        .trend-down { background: rgba(239, 68, 68, 0.1); color: ${BRAND.danger}; }
        .trend-neutral { background: rgba(107, 114, 128, 0.1); color: ${BRAND.muted}; }
        .range-btn { padding: 8px 20px; border-radius: 20px; font-weight: 500; border: 2px solid #e5e7eb; background: white; color: ${BRAND.muted}; transition: all 0.2s ease; cursor: pointer; }
        .range-btn:hover { border-color: ${BRAND.primary}; color: ${BRAND.primary}; }
        .range-btn.active { background: ${BRAND.primary}; color: white; border-color: transparent; }
        .top-link-item { padding: 1rem; border-bottom: 1px solid #f3f4f6; transition: all 0.2s ease; }
        .top-link-item:hover { background: ${BRAND.primarySoft}; }
        .top-link-item:last-child { border-bottom: none; }
      `}</style>

      <div className="analytics-container">
        {/* Decorative orbs */}
        <div className="decorative-orb" style={{ top: "10%", right: "5%", width: "300px", height: "300px", background: `radial-gradient(circle, ${BRAND.primarySoft} 0%, transparent 70%)` }} />
        <div className="decorative-orb" style={{ bottom: "20%", left: "10%", width: "250px", height: "250px", background: `radial-gradient(circle, rgba(34,197,94,0.06) 0%, transparent 70%)` }} />

        <div className="container py-4" style={{ position: "relative", zIndex: 1 }}>
          <Breadcrumbs items={breadcrumbItems} />

          {/* Header */}
          <div className="page-header-analytics">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="mb-2" style={{ fontSize: "2rem", fontWeight: "700" }}>📊 Analytics Overview</h1>
                <p className="mb-0" style={{ opacity: 0.9 }}>Comprehensive insights across all your links</p>
              </div>
              <div className="d-flex gap-2">
                {["7d", "30d", "90d"].map((range) => (
                  <button
                    key={range}
                    className={`range-btn ${dateRange === range ? "active" : ""}`}
                    onClick={() => setDateRange(range)}
                  >
                    {range.replace("d", " days")}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="row g-4 mb-4">
            <div className="col-md-3">
              <div className="stat-card">
                <div className="stat-icon" style={{ border: `solid 1px ${BRAND.success}` }}>🎯</div>
                <h6 style={{ color: BRAND.muted, fontSize: "0.875rem", fontWeight: 600, marginBottom: "0.5rem" }}>Total Clicks</h6>
                <h2 style={{ color: BRAND.text, fontWeight: 700, marginBottom: "0.5rem" }}>{totalClicks.toLocaleString()}</h2>
                <span className={`trend-badge trend-${clicksTrend.trend}`}>{clicksTrend.trend === "up" ? "↑" : clicksTrend.trend === "down" ? "↓" : "−"} {clicksTrend.value}%</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="stat-card">
                <div className="stat-icon" style={{ border: `solid 1px ${BRAND.success}` }}>🔗</div>
                <h6 style={{ color: BRAND.muted, fontSize: "0.875rem", fontWeight: 600, marginBottom: "0.5rem" }}>Total Links</h6>
                <h2 style={{ color: BRAND.text, fontWeight: 700, marginBottom: "0.5rem" }}>{totalLinks.toLocaleString()}</h2>
                <p style={{ color: BRAND.muted, fontSize: "0.875rem", margin: 0 }}>{activeLinks} active</p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="stat-card">
                <div className="stat-icon" style={{ border: `solid 1px ${BRAND.primary}` }}>📱</div>
                <h6 style={{ color: BRAND.muted, fontSize: "0.875rem", fontWeight: 600, marginBottom: "0.5rem" }}>QR Scans</h6>
                <h2 style={{ color: BRAND.text, fontWeight: 700, marginBottom: "0.5rem" }}>{totalQrScans.toLocaleString()}</h2>
                <p style={{ color: BRAND.muted, fontSize: "0.875rem", margin: 0 }}>From QR codes</p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="stat-card">
                <div className="stat-icon" style={{ border: `solid 1px ${BRAND.danger}` }}>📈</div>
                <h6 style={{ color: BRAND.muted, fontSize: "0.875rem", fontWeight: 600, marginBottom: "0.5rem" }}>Avg per Link</h6>
                <h2 style={{ color: BRAND.text, fontWeight: 700, marginBottom: "0.5rem" }}>{avgClicksPerLink.toLocaleString()}</h2>
                <p style={{ color: BRAND.muted, fontSize: "0.875rem", margin: 0 }}>Clicks per link</p>
              </div>
            </div>
          </div>

          {/* Clicks Over Time */}
          <div className="chart-card mb-4">
            <h5 style={{ color: BRAND.text, fontWeight: 600, marginBottom: "1.5rem" }}>📊 Clicks Over Time</h5>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={dailyClicks}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke={BRAND.muted} style={{ fontSize: "12px" }} />
                <YAxis stroke={BRAND.muted} style={{ fontSize: "12px" }} />
                <Tooltip contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
                <Legend />
                <Line type="monotone" dataKey="clicks" stroke={BRAND.primary} strokeWidth={3} dot={{ fill: BRAND.primary, r: 4 }} name="Clicks" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Top Links */}
          <div className="chart-card">
            <h5 style={{ color: BRAND.text, fontWeight: 600, marginBottom: "1.5rem" }}>🏆 Top 5 Performing Links</h5>
            {topLinks.length > 0 ? (
              topLinks.slice(0, 10).map((link, index) => (
                <div key={index} className="top-link-item d-flex justify-content-between align-items-center">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span style={{ background: BRAND.primary, color: "white", width: "28px", height: "28px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.875rem", fontWeight: 600 }}>{index + 1}</span>
                      <span style={{ color: BRAND.primary, fontWeight: 600, fontSize: "0.9rem" }}>{window.location.origin}/{link.shortCode}</span>
                    </div>
                    <p className="text-truncate mb-0" style={{ color: BRAND.muted, fontSize: "0.875rem", maxWidth: "600px" }}>{link.originalUrl}</p>
                  </div>
                  <div className="text-end">
                    <div style={{ background: BRAND.primary, color: "white", padding: "6px 16px", borderRadius: "20px", fontSize: "0.875rem", fontWeight: 600 }}>
                      {link.clicks.toLocaleString()} clicks
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-5" style={{ color: BRAND.muted }}>
                <p className="mb-0">No link data available</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <WhatsAppChat position="right" />
      <MiniFooter />
    </DashboardLayout>
  )
}
